<script setup lang="ts">
import type { CourseTeaser } from '~/composables/useCourse';
import { format } from 'date-fns';
import type { Speciality } from "@mui/types/courses";
import Arrow from '@/assets/images/illustrations/arrow.svg';
import { CourseType } from "~/enums/course.enums";

const props = withDefaults(defineProps<{
  course: CourseTeaser;
  updatedVersion: boolean;
  sunsetAt: string | null;
}>(), {
  updatedVersion: false,
  sunsetAt: null,
});

const { getImageUrl } = useCmsMedia();

const isCourse = computed(() => props.course?.type === CourseType.Course);
const isWorkshop = computed(() => props.course?.type === CourseType.Workshop);
const isUpdatedVersion = computed(() => props.updatedVersion);
const hasSunsetDate = computed(() => props.sunsetAt !== null);

const formattedSunsetDate = computed(() => {
  return props.sunsetAt ? format(new Date(props.sunsetAt), "MMM do") : null;
});

const formattedSunsetDateLong = computed(() => {
  return props.sunsetAt ? format(new Date(props.sunsetAt), "MMMM do") : null;
});

const titleText = computed(() => {
  if (hasSunsetDate.value) {
    return `The older ${isWorkshop.value ? 'workshop' : 'course'} disappears on ${formattedSunsetDate.value}!`;
  }

  if (isUpdatedVersion.value) {
    return isCourse.value ? 'New, updated course available!' : 'New, updated workshop available!';
  }

  return isCourse.value ? 'Preliminary course' : 'Associated workshop';
});

const descriptionText = computed(() => {
  if (hasSunsetDate.value) {
    return `
      Please note that your progress will not carry over to the newer version of the course.<br /><br />
      If you would like to complete the older course and download your certificate,
      you have until <strong>${formattedSunsetDateLong.value}</strong>.`;
  }

  if (isUpdatedVersion.value) {
    return 'A newer version of this course is available. Please note that your progress is not carried over.';
  }

  return isCourse.value
    ? 'The concepts of the cases in this workshop are taught in this associated course.'
    : 'Deepen your knowledge by practicing cases that build on what you learned in the course.';
});
</script>

<template>
  <div class="course-related-content">
    <div class="column column--left">
      <div class="title-and-description">
        <h2 class="title">
          {{titleText}}
        </h2>
        <div
          v-sanitize-html="descriptionText"
          class="description"
        />
      </div>
      <div class="arrow">
        <Arrow />
      </div>
    </div>
    <div class="column column--right">
      <MCourseCard
        :id="course.id"
        :key="`course-card-${course.id}`"
        :cme-credits="course.cmeCredits"
        :duration-minutes="course.duration"
        :title="course.title"
        :target-url="`/courses/${course.id}`"
        :teachers="course.teachers"
        :specialization="[course.specialization as Speciality]"
        :background-image="getImageUrl(course.backgroundPattern)"
        :teacher-illustration="getImageUrl(course.teacherIllustration)"
        :progress="course.progress"
        :lessons="course.lessons"
        :quizzes="course.quizzes"
        :cases="course.cases"
        :type="course.type"
        :is-free-cme-course="course.isFreeCmeCourse"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@mui/assets/styles/utility.scss';
@import '@mui/assets/styles/breakpoints.scss';
@import '@mui/assets/styles/font-styles.scss';

.course-related-content {
  @include container-sizer(page);

  display: grid;
  gap: 2em;
  grid-template-columns: 1fr;
  @include breakpoint('medium') {
    grid-template-columns: 1fr 1fr;
  }

  padding: 2em 1em;

  .column--left {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr;
    @include breakpoint('medium') {
      grid-template-columns: 2fr 1fr;
    }

    .title-and-description {
      display: flex;
      flex-direction: column;
      align-items: center;
      @include breakpoint('medium') {
        align-items: flex-start;
      }

      .title {
        @include f-32-bold;
      }
      .description {
        @include f-18-regular;
      }
    }

    .arrow {
      display: none;
      @include breakpoint('medium') {
        display: flex;
        margin: 0 auto;
      }

      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>